// src/pages/FilaEmpresasPage.js
// Visualização DOMAIN-FIRST do fuzzy match pendente.
//
// Por que domain-first: a unidade real de decisão é o DOMÍNIO. 296 matches se
// concentram em 92 domínios — top 7 (google.com, gmail.com, onedrive.com...)
// somam ~46% dos matches. Ignorar/aceitar 1 vez no domínio resolve N matches.
//
// Mantém:
//   - query useMatchPendente (sem mudança de backend)
//   - PendingCard para ações INDIVIDUAIS dentro do accordion (Criar nova, Ignorar)
//
// Adiciona:
//   - Agrupamento client-side por input_domain (useMemo)
//   - Heurística: provider | saas | re_attention | company (badge visual)
//   - Tabela densa + accordion por domínio
//   - FilterChips por categoria
//   - useBulkSelection com chave=domain (infra para próximo goal)
//
// Ações de domínio:
//   - "Ignorar" por linha → resolve_empresa_match(ignored) em todos os matches do
//     domínio + sync best-effort UPDATE email_domain_observations(not_applicable)
//   - "Vincular" por linha → busca empresa (popover) → UPSERT dominios_empresa
//     (tipo_empresa='operacional') + RPC matched em todos os matches + sync EDO tracked
//   - "Criar" por linha → QuickCreateEmpresa (nome pré-preenchido) → mesmos efeitos
//   - "Ignorar domínios selecionados" via BulkActionBar (confirm reforçado se algum
//     selecionado for re_attention/company)

import React, { useEffect, useMemo, useState } from 'react';
import {
  ListChecks, CheckCircle2, PlusCircle, XCircle, Building2,
  RefreshCw, ChevronRight, ChevronDown, Link as LinkIcon, Search, X,
} from 'lucide-react';
import AppHeader from '../components/layout/AppHeader';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import useBulkSelection from '../components/shared/useBulkSelection';
import BulkActionBar from '../components/shared/BulkActionBar';
import SelectAllCheckbox from '../components/shared/SelectAllCheckbox';
import FilterChips from '../components/shared/FilterChips';
import { useAuth } from '../hooks/useAuth';
import { useMatchPendente } from '../hooks/useMatchPendente';
import { QuickCreateEmpresa } from '../components/QuickCreateModals';
import { supabase } from '../supabaseClient';

const ALL_FILTER = '__all';

// ---------------------------------------------------------------------------
// Heurística de classificação (3 listas + 1 regex)
// ---------------------------------------------------------------------------

const PROVIDERS = new Set([
  'google.com', 'gmail.com', 'onedrive.com', 'outlook.com', 'hotmail.com',
  'icloud.com', 'yahoo.com', 'yahoo.com.br', 'googlemail.com',
  'mx.uol.com.br', 'uol.com.br', 'accounts.google.com',
]);

const SAAS_TOOLS = new Set([
  'anthropic.com', 'mail.anthropic.com', 'email.claude.com',
  'supabase.com', 'apollo.io', 'mail.apollo.io', 'apollomailtester.com',
  'stripe.com', 'shopee.com.br', 'mail.shopee.com.br',
  'taskade.com', 'openai.com', 'tm.openai.com', 'email.openai.com',
  'notify.microsoft.com',
  'inter.co', 'comunicacao.inter.co', 'relacionamento.inter.co',
  'test.mailpool.io', 'responsivecities.com',
]);

// Não é decisão automática — apenas sinaliza "olhar com atenção".
const RE_PATTERN = /vendas|parcerias|parceiro|incorpora|construt|imobili|house|lan[çc]a|corret/i;

function classifyDomain(domain) {
  if (!domain) return 'company';
  const d = String(domain).toLowerCase();
  if (PROVIDERS.has(d)) return 'provider';
  if (SAAS_TOOLS.has(d)) return 'saas';
  if (RE_PATTERN.test(d)) return 're_attention';
  return 'company';
}

// Pré-preenche company_name a partir do domínio: remove subdomínio comum
// (mail./email./inbox./notify.) + extensão (.com.br/.com/.io/.co/.br/.net/.org)
// e UPPERCASE. Ex: "mail.aston.com.br" → "ASTON".
function suggestEmpresaName(domain) {
  if (!domain) return '';
  let n = String(domain).toLowerCase();
  n = n.replace(/^(mail|email|inbox|notify|hello|contact|info|noreply|no-reply|news|newsletter|comunicacao|relacionamento)\./i, '');
  n = n.replace(/\.com\.br$|\.com$|\.io$|\.co$|\.br$|\.net$|\.org$|\.app$/i, '');
  return n.toUpperCase();
}

const CATEGORY_META = {
  provider:     { label: 'Provedor',     tone: 'bg-slate-100 text-slate-700 border-slate-200',     chipColor: 'slate'   },
  saas:         { label: 'SaaS',         tone: 'bg-blue-50   text-blue-800   border-blue-200',     chipColor: 'blue'    },
  re_attention: { label: 'RE — revisar', tone: 'bg-amber-50  text-amber-800  border-amber-200',    chipColor: 'amber'   },
  company:      { label: 'Empresa',      tone: 'bg-slate-50  text-slate-700  border-slate-200',    chipColor: 'slate'   },
};

// ---------------------------------------------------------------------------
// Helpers visuais
// ---------------------------------------------------------------------------

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function fmtDateShort(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function SourceBadge({ source }) {
  const tone = source === 'apollo' ? 'bg-emerald-100 text-emerald-800'
    : source === 'email_domain' ? 'bg-blue-100 text-blue-800'
    : 'bg-slate-100 text-slate-700';
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${tone}`}>{source || '—'}</span>;
}

function CategoryBadge({ category }) {
  const meta = CATEGORY_META[category] || CATEGORY_META.company;
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${meta.tone}`}>
      {meta.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// CandidateCard + PendingCard (ações INDIVIDUAIS — usado dentro do accordion)
// ---------------------------------------------------------------------------

function CandidateCard({ cand, onPick, busy }) {
  const sim = cand.similarity != null
    ? `${Math.round(cand.similarity * 100)}%`
    : (cand.score != null ? `${Math.round(cand.score * 100)}%` : '—');
  return (
    <button
      onClick={() => onPick(cand)}
      disabled={busy}
      className="flex items-center justify-between gap-2 px-3 py-2 bg-white border border-slate-200 rounded hover:border-blue-400 hover:bg-blue-50 w-full text-left disabled:opacity-50"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Building2 size={13} className="text-slate-400 flex-shrink-0" />
        <span className="text-xs font-medium text-slate-800 truncate">
          {cand.empresa || cand.company_name || cand.name || cand.id}
        </span>
      </div>
      <span className="text-[10px] text-slate-500 flex-shrink-0 tabular-nums">{sim}</span>
    </button>
  );
}

function PendingCard({ item, onResolved }) {
  const { user } = useAuth();
  const { resolve } = useMatchPendente();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  const candidates = Array.isArray(item.candidates)
    ? item.candidates
    : (item.candidates && Array.isArray(item.candidates.matches))
      ? item.candidates.matches
      : [];

  const handleResolve = async (action, empresaId) => {
    setBusy(true);
    setError(null);
    try {
      await resolve({
        pendingId: item.id,
        action,
        empresaId: empresaId || null,
        resolvedBy: user?.email || user?.id || 'unknown',
      });
      onResolved();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <SourceBadge source={item.source_type} />
            <span className="text-[10px] text-slate-400">{fmtDateTime(item.created_at)}</span>
          </div>
          <div className="text-sm font-semibold text-slate-800 truncate">{item.input_name || '— sem nome —'}</div>
          {item.source_ref && (
            <div className="text-[10px] text-slate-400 truncate mt-0.5">ref: {item.source_ref}</div>
          )}
        </div>
      </div>

      {candidates.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
            Candidatos ({candidates.length})
          </div>
          <div className="space-y-1.5">
            {candidates.map((c, idx) => (
              <CandidateCard
                key={c.id || c.empresa_id || idx}
                cand={c}
                busy={busy}
                onPick={(picked) => handleResolve('matched', picked.id || picked.empresa_id)}
              />
            ))}
          </div>
        </div>
      )}

      {error && <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

      <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
        <button
          onClick={() => setCreating(true)}
          disabled={busy}
          className="flex-1 px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1"
        >
          <PlusCircle size={12} /> Criar nova
        </button>
        <button
          onClick={() => handleResolve('ignored', null)}
          disabled={busy}
          className="flex-1 px-3 py-1.5 bg-slate-100 text-slate-700 rounded text-xs hover:bg-slate-200 disabled:opacity-50 flex items-center justify-center gap-1"
        >
          <XCircle size={12} /> Ignorar
        </button>
      </div>

      {creating && (
        <QuickCreateEmpresa
          nomeInicial={item.input_name || ''}
          onClose={() => setCreating(false)}
          onCreated={async (nova) => {
            setCreating(false);
            await handleResolve('created_new', nova.id);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LinkEmpresaPopover — busca empresa por nome (ilike) e seleciona
// ---------------------------------------------------------------------------

function LinkEmpresaPopover({ group, onClose, onPick }) {
  const [query, setQuery] = useState(suggestEmpresaName(group.domain));
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from('empresas')
        .select('id, company_name, empresa')
        .or(`company_name.ilike.%${q}%,empresa.ilike.%${q}%`)
        .order('company_name', { ascending: true })
        .limit(10);
      if (!cancelled) {
        setResults(data || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [query]);

  return (
    <div className="fixed inset-0 bg-black/50 z-[10010] flex items-start justify-center pt-20 px-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Vincular domínio</div>
            <div className="text-sm font-semibold text-slate-800 font-mono">{group.domain}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {group.matchCount} match{group.matchCount === 1 ? '' : 'es'} pendente{group.matchCount === 1 ? '' : 's'} serão resolvidos
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 space-y-2">
          <div className="flex items-center gap-2 border border-slate-300 rounded px-2 py-1.5">
            <Search size={13} className="text-slate-400 flex-shrink-0" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar empresa por nome..."
              autoFocus
              className="flex-1 text-xs outline-none"
            />
          </div>
          <div className="min-h-[120px] max-h-[280px] overflow-y-auto">
            {loading && (
              <div className="text-xs text-slate-500 py-4 text-center">Buscando...</div>
            )}
            {!loading && query.trim().length < 2 && (
              <div className="text-xs text-slate-400 py-4 text-center">
                Digite pelo menos 2 caracteres
              </div>
            )}
            {!loading && query.trim().length >= 2 && results.length === 0 && (
              <div className="text-xs text-slate-500 py-4 text-center">
                Nenhuma empresa encontrada com "{query.trim()}"
              </div>
            )}
            {results.map((emp) => (
              <button
                key={emp.id}
                onClick={() => onPick(emp)}
                className="w-full text-left px-2 py-1.5 hover:bg-blue-50 rounded text-xs text-slate-800 flex items-center gap-2"
              >
                <Building2 size={13} className="text-slate-400 flex-shrink-0" />
                <span className="truncate">{emp.company_name || emp.empresa || emp.id}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page principal — DOMAIN-FIRST
// ---------------------------------------------------------------------------

export default function FilaEmpresasPage() {
  const { data, loading, error, refetch, resolve } = useMatchPendente();
  const { user } = useAuth();
  const sel = useBulkSelection();
  const [categoryFilter, setCategoryFilter] = useState(ALL_FILTER);
  const [expanded, setExpanded] = useState(() => new Set());
  const [toast, setToast] = useState(null);
  const [linkingGroup, setLinkingGroup] = useState(null); // group being linked via popover
  const [creatingGroup, setCreatingGroup] = useState(null); // group being created via QuickCreate

  const showToast = (message, tone = 'success') => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 2500);
  };

  // Core: resolve todos os matches do domínio + sync best-effort com
  // email_domain_observations. Em falha de match: throw (caller decide se para).
  // Retorna o número de matches resolvidos com sucesso.
  const ignoreDomainCore = async (domain, matchIds, category) => {
    const resolvedBy = user?.email || user?.id || 'unknown';
    let resolved = 0;
    for (const id of matchIds) {
      try {
        await resolve({ pendingId: id, action: 'ignored', empresaId: null, resolvedBy });
        resolved += 1;
      } catch (err) {
        throw Object.assign(
          new Error(`Falhou no match ${id} (${resolved}/${matchIds.length} OK): ${err.message || err}`),
          { resolved },
        );
      }
    }

    // Best-effort sync com Cobertura de Domínios
    if (domain && domain !== '(sem domínio)') {
      try {
        const { data: existing, error: selErr } = await supabase
          .from('email_domain_observations')
          .select('tracking_status')
          .eq('domain', domain)
          .maybeSingle();
        if (selErr) throw selErr;

        if (existing && existing.tracking_status !== 'tracked') {
          const catLabel = CATEGORY_META[category]?.label || category;
          const { error: updErr } = await supabase
            .from('email_domain_observations')
            .update({
              tracking_status: 'not_applicable',
              not_applicable_reason: `Ignorado na Fila Empresas: ${catLabel}`,
              updated_at: new Date().toISOString(),
              updated_by: resolvedBy,
            })
            .eq('domain', domain);
          if (updErr) throw updErr;
        }
      } catch (syncErr) {
        // Best-effort: log + segue
        // eslint-disable-next-line no-console
        console.warn(`EDO sync best-effort falhou para ${domain}:`, syncErr.message || syncErr);
      }
    }

    return resolved;
  };

  // Ignorar 1 domínio (botão da linha)
  const handleIgnoreDomain = async (group) => {
    const ok = window.confirm(
      `Ignorar domínio ${group.domain}? Isso resolve ${group.matchCount} match${group.matchCount === 1 ? '' : 'es'} pendente${group.matchCount === 1 ? '' : 's'}.`,
    );
    if (!ok) return;
    try {
      const resolved = await ignoreDomainCore(group.domain, group.matchIds, group.category);
      await refetch();
      // Limpa seleção desse domínio se estava selecionado
      if (sel.isSelected(group.domain)) sel.toggle(group.domain);
      showToast(
        `${group.domain} ignorado (${resolved} match${resolved === 1 ? '' : 'es'} resolvido${resolved === 1 ? '' : 's'})`,
      );
    } catch (err) {
      await refetch();
      showToast(`Erro: ${err.message || err}`, 'error');
    }
  };

  // ---------- VINCULAR / CRIAR ----------

  // Core: aplica vínculo de domínio → empresa.
  // 1. Verifica conflito em dominios_empresa (já vinculado a outra)
  // 2. UPSERT dominios_empresa(dominio, empresa_id, tipo_empresa='operacional')
  // 3. RPC resolve_empresa_match('matched') para cada matchId
  // 4. Sync EDO: tracking_status='tracked', empresa_id (com conflict-check)
  const linkDomainCore = async (group, empresa, opts = {}) => {
    const { skipConflictPrompt = false, matchAction = 'matched' } = opts;
    const resolvedBy = user?.email || user?.id || 'unknown';

    if (!group.domain || group.domain === '(sem domínio)') {
      throw new Error('Não é possível vincular um item sem domínio');
    }

    // 1) Conflito em dominios_empresa
    if (!skipConflictPrompt) {
      const { data: existingDE, error: deErr } = await supabase
        .from('dominios_empresa')
        .select('empresa_id, empresas:empresa_id (company_name, empresa)')
        .eq('dominio', group.domain)
        .maybeSingle();
      if (deErr) throw deErr;
      if (existingDE && existingDE.empresa_id && existingDE.empresa_id !== empresa.id) {
        const otherName = existingDE.empresas?.company_name || existingDE.empresas?.empresa || existingDE.empresa_id;
        const ok = window.confirm(
          `Domínio ${group.domain} já vinculado a ${otherName} em dominios_empresa. Sobrescrever?`,
        );
        if (!ok) throw new Error('Vínculo cancelado pelo usuário');
      }
    }

    // 2) UPSERT dominios_empresa (UNIQUE(dominio))
    const now = new Date().toISOString();
    const { error: upDeErr } = await supabase
      .from('dominios_empresa')
      .upsert(
        {
          dominio: group.domain,
          empresa_id: empresa.id,
          tipo_empresa: 'operacional',
          confianca: 1.0,
          atualizado_em: now,
          atualizado_por: resolvedBy,
        },
        { onConflict: 'dominio' },
      );
    if (upDeErr) throw upDeErr;

    // 3) Resolver matches via RPC
    let resolved = 0;
    for (const id of group.matchIds) {
      try {
        await resolve({ pendingId: id, action: matchAction, empresaId: empresa.id, resolvedBy });
        resolved += 1;
      } catch (err) {
        throw Object.assign(
          new Error(`Falhou no match ${id} (${resolved}/${group.matchIds.length} OK): ${err.message || err}`),
          { resolved },
        );
      }
    }

    // 4) Sync best-effort com Cobertura (skip se conflito)
    try {
      const { data: edo } = await supabase
        .from('email_domain_observations')
        .select('tracking_status, empresa_id, empresas:empresa_id (company_name, empresa)')
        .eq('domain', group.domain)
        .maybeSingle();

      if (edo) {
        const alreadyOk = edo.tracking_status === 'tracked' && edo.empresa_id === empresa.id;
        if (!alreadyOk) {
          let proceed = true;
          if (edo.tracking_status === 'tracked' && edo.empresa_id && edo.empresa_id !== empresa.id) {
            const otherName = edo.empresas?.company_name || edo.empresas?.empresa || edo.empresa_id;
            proceed = window.confirm(
              `Cobertura: ${group.domain} já tracked em ${otherName}. Sobrescrever?`,
            );
          }
          if (proceed) {
            const { error: edoErr } = await supabase
              .from('email_domain_observations')
              .update({
                tracking_status: 'tracked',
                empresa_id: empresa.id,
                updated_at: now,
                updated_by: resolvedBy,
              })
              .eq('domain', group.domain);
            if (edoErr) throw edoErr;
          }
        }
      }
    } catch (syncErr) {
      // eslint-disable-next-line no-console
      console.warn(`EDO sync best-effort falhou para ${group.domain}:`, syncErr.message || syncErr);
    }

    return resolved;
  };

  const handleVincular = async (group, empresa) => {
    try {
      const resolved = await linkDomainCore(group, empresa);
      await refetch();
      if (sel.isSelected(group.domain)) sel.toggle(group.domain);
      const nome = empresa.company_name || empresa.empresa || 'empresa';
      showToast(`${group.domain} vinculado a ${nome} (${resolved} match${resolved === 1 ? '' : 'es'})`);
    } catch (err) {
      await refetch();
      showToast(`Erro: ${err.message || err}`, 'error');
    }
  };

  const handleCreatedFromDomain = async (group, novaEmpresa) => {
    // novaEmpresa: { id, ... } vindo de QuickCreateEmpresa.onCreated
    try {
      const resolved = await linkDomainCore(group, novaEmpresa, {
        matchAction: 'created_new',
      });
      await refetch();
      if (sel.isSelected(group.domain)) sel.toggle(group.domain);
      const nome = novaEmpresa.company_name || novaEmpresa.empresa || 'empresa';
      showToast(`${group.domain} → ${nome} criada (${resolved} match${resolved === 1 ? '' : 'es'})`);
    } catch (err) {
      await refetch();
      showToast(`Erro: ${err.message || err}`, 'error');
    }
  };

  // Bulk ignorar domínios selecionados
  const bulkIgnoreDomains = async () => {
    const selectedDomains = sel.getSelectedIds();
    if (selectedDomains.length === 0) return;

    // Recupera categorias dos selecionados pra confirm reforçado
    const selectedSet = new Set(selectedDomains);
    const selectedGroups = domainGroups.filter((g) => selectedSet.has(g.domain));
    const hasRisky = selectedGroups.some((g) => g.category === 're_attention' || g.category === 'company');

    if (hasRisky) {
      const okExtra = window.confirm(
        'Alguns domínios selecionados podem ser empresas reais (RE — revisar / Empresa). Tem certeza?',
      );
      if (!okExtra) throw new Error('Bulk cancelado pelo usuário');
    }

    let domainsOk = 0;
    let matchesOk = 0;
    let failedAt = null;

    for (const g of selectedGroups) {
      try {
        const resolved = await ignoreDomainCore(g.domain, g.matchIds, g.category);
        domainsOk += 1;
        matchesOk += resolved;
      } catch (err) {
        // Conta os parcialmente resolvidos deste domínio
        if (typeof err.resolved === 'number') matchesOk += err.resolved;
        failedAt = { domain: g.domain, msg: err.message || String(err) };
        break;
      }
    }

    await refetch();

    if (failedAt) {
      // Mantém seleção dos não processados (+ falho)
      const processed = new Set(selectedGroups.slice(0, domainsOk).map((g) => g.domain));
      sel.deselectAll();
      const remaining = selectedDomains.filter((d) => !processed.has(d));
      if (remaining.length > 0) sel.selectAll(remaining);
      showToast(
        `Erro em ${failedAt.domain} após ${domainsOk} domínio(s): ${failedAt.msg}`,
        'error',
      );
      throw new Error(failedAt.msg);
    }

    showToast(
      `${domainsOk} ${domainsOk === 1 ? 'domínio ignorado' : 'domínios ignorados'} (${matchesOk} match${matchesOk === 1 ? '' : 'es'} resolvido${matchesOk === 1 ? '' : 's'})`,
    );
  };

  // Agrupa por input_domain client-side.
  // Items sem domínio (null/vazio) ficam num bucket '(sem domínio)'.
  const { domainGroups, filterChips, filteredGroups } = useMemo(() => {
    const items = Array.isArray(data) ? data : [];
    const map = new Map();
    for (const it of items) {
      const d = (it.input_domain || '').trim() || '(sem domínio)';
      let g = map.get(d);
      if (!g) {
        g = {
          domain: d,
          matchCount: 0,
          matches: [],
          matchIds: [],
          sampleRefs: [],
          firstSeen: it.created_at,
          lastSeen: it.created_at,
          category: classifyDomain(d),
        };
        map.set(d, g);
      }
      g.matchCount += 1;
      g.matches.push(it);
      g.matchIds.push(it.id);
      if (g.sampleRefs.length < 3 && it.input_name) g.sampleRefs.push(it.input_name);
      if (it.created_at && (!g.firstSeen || it.created_at < g.firstSeen)) g.firstSeen = it.created_at;
      if (it.created_at && (!g.lastSeen || it.created_at > g.lastSeen)) g.lastSeen = it.created_at;
    }

    const groups = Array.from(map.values()).sort((a, b) => b.matchCount - a.matchCount);

    // Chips por categoria (auto-conta)
    const catCounts = new Map();
    for (const g of groups) catCounts.set(g.category, (catCounts.get(g.category) || 0) + 1);
    const chips = [
      { key: ALL_FILTER, label: 'Todos', count: groups.length },
      ...['provider', 'saas', 're_attention', 'company']
        .filter((c) => catCounts.get(c) > 0)
        .map((c) => ({
          key: c,
          label: CATEGORY_META[c].label,
          count: catCounts.get(c),
          color: CATEGORY_META[c].chipColor,
        })),
    ];

    const filtered = categoryFilter === ALL_FILTER
      ? groups
      : groups.filter((g) => g.category === categoryFilter);

    return { domainGroups: groups, filterChips: chips, filteredGroups: filtered };
  }, [data, categoryFilter]);

  const totalMatches = (data || []).length;

  const handleFilterChange = (key) => {
    setCategoryFilter(key);
    sel.deselectAll();
  };

  const toggleExpand = (domain) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  };

  const headerCount = loading
    ? 'Carregando…'
    : `${domainGroups.length} ${domainGroups.length === 1 ? 'domínio' : 'domínios'} (${totalMatches} ${totalMatches === 1 ? 'match' : 'matches'})`;

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader
        title="Fila Empresas"
        subtitle="Revisão humana do fuzzy match — agrupado por domínio"
        icon={ListChecks}
      />
      <main className="p-4 lg:p-6 max-w-6xl mx-auto space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-slate-600 font-medium">{headerCount}</div>
          <div className="flex items-center gap-3">
            {filteredGroups.length > 0 && (
              <SelectAllCheckbox
                totalCount={filteredGroups.length}
                selectedCount={sel.count}
                onSelectAll={() => sel.selectAll(filteredGroups.map((g) => g.domain))}
                onDeselectAll={sel.deselectAll}
                noun="domínios"
              />
            )}
            <button
              onClick={refetch}
              className="text-xs px-2 py-1 bg-white border border-slate-200 text-slate-700 rounded hover:bg-slate-50 flex items-center gap-1"
            >
              <RefreshCw size={12} /> Atualizar
            </button>
          </div>
        </div>

        <FilterChips
          chips={filterChips}
          activeKey={categoryFilter}
          onChange={handleFilterChange}
        />

        {loading && <LoadingSpinner label="Carregando fila..." />}
        {!loading && error && <ErrorState message={error} onRetry={refetch} />}
        {!loading && !error && (!data || data.length === 0) && (
          <EmptyState
            message="Nenhuma empresa pendente de revisão"
            hint="Quando o fuzzy match do pipeline gerar dúvida, os itens aparecem aqui."
            icon={CheckCircle2}
          />
        )}
        {!loading && !error && data && data.length > 0 && filteredGroups.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 text-center">
            <p className="text-xs text-slate-500">
              Nenhum domínio nesta categoria.{' '}
              <button
                onClick={() => handleFilterChange(ALL_FILTER)}
                className="text-blue-600 hover:underline"
              >
                Limpar filtro
              </button>
            </p>
          </div>
        )}

        {!loading && !error && filteredGroups.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left w-10"></th>
                  <th className="px-3 py-2 text-left w-8"></th>
                  <th className="px-3 py-2 text-left">Domínio</th>
                  <th className="px-3 py-2 text-right">Matches</th>
                  <th className="px-3 py-2 text-left">Categoria</th>
                  <th className="px-3 py-2 text-left">Refs exemplo</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Primeiro</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Último</th>
                  <th className="px-3 py-2 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((g) => {
                  const isSelected = sel.isSelected(g.domain);
                  const isExpanded = expanded.has(g.domain);
                  return (
                    <React.Fragment key={g.domain}>
                      <tr
                        className={`border-t border-slate-100 cursor-pointer ${
                          isSelected ? 'bg-slate-100' : 'hover:bg-slate-50'
                        }`}
                        onClick={() => toggleExpand(g.domain)}
                      >
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => sel.toggle(g.domain)}
                            aria-label={`Selecionar ${g.domain}`}
                            className="cursor-pointer w-4 h-4 accent-slate-700 rounded"
                          />
                        </td>
                        <td className="px-2 py-2 text-slate-500">
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-800 truncate max-w-[260px]" title={g.domain}>
                          {g.domain}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800">
                          {g.matchCount}
                        </td>
                        <td className="px-3 py-2">
                          <CategoryBadge category={g.category} />
                        </td>
                        <td className="px-3 py-2 text-[11px] text-slate-600 truncate max-w-[260px]" title={g.sampleRefs.join(' · ')}>
                          {g.sampleRefs.join(' · ') || '—'}
                        </td>
                        <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap">{fmtDateShort(g.firstSeen)}</td>
                        <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap">{fmtDateShort(g.lastSeen)}</td>
                        <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="inline-flex gap-1">
                            <button
                              onClick={() => setLinkingGroup(g)}
                              className="px-2 py-1 text-[11px] text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded inline-flex items-center gap-1"
                              title={
                                g.category === 're_attention'
                                  ? `Vincular ${g.domain} à empresa-mãe (pode ser house, corretora ou canal)`
                                  : `Vincular ${g.domain} a empresa existente e resolver ${g.matchCount} match${g.matchCount === 1 ? '' : 'es'}`
                              }
                            >
                              <LinkIcon size={11} />
                              {g.category === 're_attention' ? 'Vincular à empresa-mãe' : 'Vincular'}
                            </button>
                            <button
                              onClick={() => setCreatingGroup(g)}
                              className="px-2 py-1 text-[11px] text-green-700 bg-green-50 hover:bg-green-100 rounded inline-flex items-center gap-1"
                              title={
                                g.category === 're_attention'
                                  ? `Criar empresa nova para ${g.domain} (cuidado: pode ser canal de outra)`
                                  : `Criar nova empresa para ${g.domain} e resolver ${g.matchCount} match${g.matchCount === 1 ? '' : 'es'}`
                              }
                            >
                              <PlusCircle size={11} />
                              {g.category === 're_attention' ? 'Criar nova empresa' : 'Criar'}
                            </button>
                            <button
                              onClick={() => handleIgnoreDomain(g)}
                              className="px-2 py-1 text-[11px] text-slate-700 bg-slate-100 hover:bg-slate-200 rounded inline-flex items-center gap-1"
                              title={`Ignorar domínio e resolver ${g.matchCount} match${g.matchCount === 1 ? '' : 'es'}`}
                            >
                              <XCircle size={11} /> Ignorar
                            </button>
                          </div>
                          {g.category === 're_attention' && (
                            <div className="text-[10px] text-amber-700 mt-0.5">
                              Pode ser house, corretora ou canal de incorporadora
                            </div>
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-t border-slate-100 bg-slate-50">
                          <td colSpan={9} className="p-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {g.matches.map((m) => (
                                <PendingCard key={m.id} item={m} onResolved={refetch} />
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {linkingGroup && (
          <LinkEmpresaPopover
            group={linkingGroup}
            onClose={() => setLinkingGroup(null)}
            onPick={async (empresa) => {
              const g = linkingGroup;
              setLinkingGroup(null);
              await handleVincular(g, empresa);
            }}
          />
        )}

        {creatingGroup && (
          <QuickCreateEmpresa
            nomeInicial={suggestEmpresaName(creatingGroup.domain)}
            onClose={() => setCreatingGroup(null)}
            onCreated={async (nova) => {
              const g = creatingGroup;
              setCreatingGroup(null);
              await handleCreatedFromDomain(g, nova);
            }}
          />
        )}

        <BulkActionBar
          count={sel.count}
          onClear={sel.deselectAll}
          actions={[
            {
              label: 'Ignorar domínios selecionados',
              icon: XCircle,
              color: 'gray',
              confirmMessage: (n) => `Ignorar ${n} ${n === 1 ? 'domínio' : 'domínios'}? Todos os matches pendentes serão resolvidos.`,
              onClick: bulkIgnoreDomains,
            },
          ]}
        />

        {toast && (
          <div
            className={`fixed bottom-4 right-4 z-50 px-3 py-2 rounded shadow-lg text-xs font-medium ${
              toast.tone === 'error'
                ? 'bg-red-600 text-white'
                : 'bg-emerald-600 text-white'
            }`}
            role="status"
          >
            {toast.message}
          </div>
        )}
      </main>
    </div>
  );
}
